import { useEffect } from "react";
import { Outlet, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch } from "react-redux";

import { PORTAL_AUTH_CODE_STORAGE_KEY } from "../constants";
import { useFetchContactQuery } from "../services/api/contact";
import { emptySplitApi } from "../services/baseApi";
import { PortalCacheTags } from "../services/cacheTags";
import { usePortalInvoice } from "../hooks/usePortalInvoice";
import { usePortalQuote } from "../hooks/usePortalQuote";
import { useSession } from "../hooks/useSession";
import { usePortalLogRocket } from "../hooks/usePortalLogRocket";

import { Container } from "./Container";
import { Footer } from "./Footer";

export function Root() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { code } = useParams();
  const session = useSession();
  const { data: contactResult, refetch: refetchContact, isLoading: contactLoading } = useFetchContactQuery(
    { code: code ?? ``, sessionId: session?.id ?? `` },
    {
      skip: !window.location.pathname.includes(`/c/`) || !session?.id || !code,
    },
  );

  const { data: invoiceResult, refetch: refetchInvoice, isLoading: invoiceLoading } = usePortalInvoice();
  const { data: quoteResult, refetch: refetchQuote, isLoading: quoteLoading } = usePortalQuote();

  const loading = contactLoading || invoiceLoading || quoteLoading;

  usePortalLogRocket(contactResult ?? invoiceResult ?? quoteResult, session?.id);

  useEffect(() => {
    if (loading) {
      // Let loading finish first
      return;
    }
    const portalAuthCode = searchParams.get(`portalAuthCode`);

    if (!portalAuthCode) {
      return;
    }

    sessionStorage.setItem(PORTAL_AUTH_CODE_STORAGE_KEY, portalAuthCode);

    const next = new URLSearchParams(searchParams);

    next.delete(`portalAuthCode`);

    setSearchParams(next, {
      replace: true,
    });

    if (invoiceResult?.requiresVerification) {
      refetchInvoice();
    }

    if (quoteResult?.requiresVerification) {
      refetchQuote();
    }

    if (contactResult?.requiresVerification) {
      refetchContact();
    }

    // Payment methods are gated on verification even when the page itself is not
    dispatch(emptySplitApi.util.invalidateTags([PortalCacheTags.PaymentMethods]));
  }, [
    dispatch,
    searchParams,
    setSearchParams,
    invoiceResult,
    quoteResult,
    contactResult,
    refetchContact,
    refetchInvoice,
    refetchQuote,
    loading,
  ]);

  return (
    <>
      <Container>
        <Outlet />
      </Container>
      <Footer />
      <ToastContainer
        position={ `bottom-center` }
        autoClose={ 2000 }
        hideProgressBar={ false }
        newestOnTop={ true }
        closeOnClick
        draggable
        pauseOnHover
        theme={ `colored` }
      />
    </>
  );
}
